/**
 * Resolve a dot-path into a value tree to the flat list of all values it selects.
 *
 * Segments are `.`-separated. A `[]` suffix on a segment (e.g. `items[]`) — or a
 * standalone `[]` segment — means "iterate the current array and continue for each
 * element". Missing keys / non-objects collapse to nothing, so a miss returns `[]`.
 */
export function resolvePath(root: unknown, path: string): unknown[] {
  const segs = path.split(".");
  let cur: unknown[] = [root];
  for (const rawSeg of segs) {
    // A segment may carry any number of trailing `[]` array-any markers, e.g.
    // `items[]` or `matrix[][]`. Strip them off and apply them after the key step.
    let seg = rawSeg;
    let arrayDepth = 0;
    while (seg.endsWith("[]")) {
      seg = seg.slice(0, -2);
      arrayDepth += 1;
    }

    if (seg !== "") {
      cur = cur.flatMap((v) => {
        if (v == null || typeof v !== "object") return [];
        const next = (v as Record<string, unknown>)[seg];
        return next === undefined ? [] : [next];
      });
    }

    for (let i = 0; i < arrayDepth; i += 1) {
      cur = cur.flatMap((v) => (Array.isArray(v) ? v : []));
    }
  }
  return cur.filter((v) => v !== undefined);
}
